import * as React from "react";
import { Stack } from "@mui/material";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { tallyBy } from "@/utils/tally";
import type { OrderRow } from "./OrderTable";

interface OrderSummaryCardProps {
  orders: OrderRow[];
}

const OPEN_STATUSES = new Set(["PENDING", "SUBMITTED", "ACKNOWLEDGED", "PARTIALLY_FILLED"]);
const COMPLETED_STATUSES = new Set(["COMPLETED", "FILLED", "ACCEPTED"]);

/**
 * Every count here is a tally of already-fetched order records by their
 * backend-reported `status` field - never a derived financial value.
 * "Last Updated" has no backend field to source from (`ExecutionOrderDto`
 * carries no timestamp) and renders as unavailable rather than fabricated.
 */
export function OrderSummaryCard({ orders }: OrderSummaryCardProps): React.JSX.Element {
  const byStatus = tallyBy(orders, (o) => o.status);
  const openCount = Object.entries(byStatus)
    .filter(([status]) => OPEN_STATUSES.has(status))
    .reduce((sum, [, count]) => sum + count, 0);
  const completedCount = Object.entries(byStatus)
    .filter(([status]) => COMPLETED_STATUSES.has(status))
    .reduce((sum, [, count]) => sum + count, 0);

  return (
    <StatusCard title="Order Summary">
      <Stack spacing={1.5}>
        <MetricCard label="Total Orders" value={orders.length} />
        <MetricCard label="Open Orders" value={openCount} />
        <MetricCard label="Completed Orders" value={completedCount} />
        <MetricCard label="Rejected Orders" value={byStatus.REJECTED ?? 0} />
        <MetricCard label="Cancelled Orders" value={byStatus.CANCELLED ?? 0} />
        <MetricCard label="Last Updated" value={null} />
      </Stack>
    </StatusCard>
  );
}
